import type React from "react";
import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import CustomTextField from "../../../components/common/Textfield";
import CustomSelect from "../../../components/common/Select";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";

interface AddRoleModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (role: {
    title: string;
    description: string;
    permissionLevel: string;
  }) => void;
}

const AddRoleModal: React.FC<AddRoleModalProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    permissionLevel: "Manager",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    setFormData({ title: "", description: "", permissionLevel: "Manager" });
    onClose();
  };

  const handleChange = (field: string) => (e: any) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const reduxTheme = useSelector((state: RootState) => state.theme);

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          onClose={onClose}
          fullWidth
          PaperProps={
            {
              initial: { opacity: 0, scale: 0.9 },
              animate: { opacity: 1, scale: 1 },
              exit: { opacity: 0, scale: 0.9 },
              transition: { duration: 0.2 },
            } as any
          }
        >
          <DialogTitle>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" fontWeight="bold">
                Create New Role
              </Typography>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={3}>
                <CustomTextField
                  label="Role Title"
                  value={formData.title}
                  onChange={handleChange("title")}
                  fullWidth
                  required
                  variant="outlined"
                />

                <CustomTextField
                  label="Description"
                  value={formData.description}
                  onChange={handleChange("description")}
                  fullWidth
                  required
                  multiline
                  rows={3}
                  variant="outlined"
                />
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 1 }}>
              <Button
                onClick={onClose}
                sx={{ backgroundColor: reduxTheme.primary_colors[800] }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained">
                Create Role
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default AddRoleModal;
